import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Card, CardContent } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Search } from "lucide-react";
import { formatToShortDate, formatUniversalNumber } from "../../../lib/utils";
import { ChangeEventHandler, useContext, useRef } from "react";
import { BankingContext } from "../../../providers/BankingProvider";
import { DatePicker } from "../../../components/ui/datepicker";
import { TableSkeleton } from "../../../components/Skeleton/TableSkeleton";
import { DataTable } from "../../../components/ui/data-table";
import { CreditMemoContext } from "../../../providers/CreditMemoProvider";
import CustomReactSelect from "../../../components/ui/custom-react-select";

interface MatchTransactionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const MatchTransactionDialog = ({
  open,
  onOpenChange,
}: MatchTransactionDialogProps) => {
  const { editTransaction } = useContext(BankingContext);

  const {
    handlePageChange,
    transactionfilters,
    memofilters,
    invoices,
    filterByOptions,
    handleFilterChange,
    handlePageSizeChange,
    pagination,
    rowSelection,
    setRowSelection,
    creditMemoRowSelection,
    setCreditMemoRowSelection,
    payments,
    handlePaymentChange,
    isLoading,
    creditMemos,
    creditMemoPagiation,
    creditMemoPayments,
    handleMatch,
    error,
    handleCloseDialog,
    getPaymentError,
  } = useContext(CreditMemoContext);

  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const creditMemoInputRefs = useRef<{
    [key: string]: HTMLInputElement | null;
  }>({});

  const InvoiceColumns = [
    {
      id: "select",
      header: ({ table }: any) => (
        <input
          type="checkbox"
          checked={table.getIsAllRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      size: 30,
      minSize: 30,
      maxSize: 30,
      cell: ({ row }: any) => (
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "refInvoiceDate",
      header: "Date",
      cell: ({ row }: any) => formatToShortDate(row.original.refInvoiceDate),
    },
    {
      accessorKey: "refInvoiceId",
      header: "Ref Number",
      cell: ({ row }: { row: RowType }) => {
        return <span>{row.original.refInvoiceId}</span>;
      },
    },
    {
      accessorKey: "customerName",
      header: "Payee",
      cell: ({ row }: any) => row.original.customerName, // Set value from `customerName`
    },
    {
      accessorKey: "totalAmt",
      header: () => (
        <span style={{ width: "50px" }} className="w-full block text-right">
          Transaction Amount
        </span>
      ),
      cell: ({ row }: { row: RowType }) => {
        const amount: number = row.original.totalAmt;
        return (
          <span style={{ width: "50px" }} className="text-right block">
            {formatUniversalNumber(amount)}
          </span>
        );
      },
    },
    {
      accessorKey: "outstandingAmt",
      header: () => (
        <span
          style={{ width: "50px" }}
          className="w-full justify-end text-right block"
        >
          Open Balance
        </span>
      ),
      cell: ({ row }: { row: RowType }) => {
        const amount: number = row.original.outstandingAmt;
        return (
          <span style={{ width: "50px" }} className="text-right block">
            {formatUniversalNumber(amount)}
          </span>
        );
      },
    },
    {
      id: "amount",
      accessorKey: "",
      header: "Payment",
      cell: ({ row }: { row: RowType }) => {
        const invoiceId = row.original.invoiceId;
        const totalAmt = row.original.outstandingAmt;
        const paymentObj = payments[invoiceId] || {
          value: "",
          totalAmt: totalAmt,
        };
        const value = paymentObj.value;

        const isChecked = row.getIsSelected?.() ?? false;

        const error = isChecked ? getPaymentError(value, totalAmt) : undefined;

        return (
          <div>
            <Input
              ref={(el) => {
                inputRefs.current[invoiceId] = el ?? null;
                return;
              }}
              value={value}
              onChange={(e) => {
                handlePaymentChange(
                  "invoice",
                  invoiceId,
                  e.target.value,
                  totalAmt
                );
                // Focus the input again after value change
                setTimeout(() => {
                  inputRefs.current[invoiceId]?.focus();
                }, 0);
              }}
              placeholder="Payment"
              className={`min-w-[50px]`}
              type="number"
              disabled={!isChecked}
              error={error as string | undefined}
            />
          </div>
        );
      },
    },
  ];

  interface RowType {
    original: any;
    getIsSelected: () => boolean;
    getToggleSelectedHandler: () =>
      | ChangeEventHandler<HTMLInputElement>
      | undefined;
  }

  const MemoColumns = [
    {
      id: "select",
      header: ({ table }: any) => (
        <input
          type="checkbox"
          checked={table.getIsAllRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      size: 30,
      minSize: 30,
      maxSize: 30,
      cell: ({ row }: { row: RowType }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "memoDate",
      header: "Date",
      cell: ({ row }: { row: RowType }) =>
        formatToShortDate(row.original.memoDate),
    },
    {
      accessorKey: "externalId",
      header: "Memo Number",
      cell: ({ row }: { row: RowType }) => (
        <span className="truncate min-w-0 block">
          {row.original.externalId}
        </span>
      ),
    },
    {
      accessorKey: "customerName",
      header: "Payee",
      cell: ({ row }: { row: RowType }) => row.original.customerName, // Set value from `customerName`
    },
    {
      accessorKey: "totalAmt",
      header: () => (
        <span style={{ width: "50px" }} className="w-full text-right block">
          Transaction Amount
        </span>
      ),
      cell: ({ row }: { row: RowType }) => {
        const amount: number = row.original.totalAmt;
        return (
          <span style={{ width: "50px" }} className="text-right block">
            {formatUniversalNumber(amount)}
          </span>
        );
      },
    },
    {
      accessorKey: "outstandingAmount",
      header: () => (
        <span
          style={{ width: "50px" }}
          className="w-full justify-end text-right block"
        >
          Open Balance
        </span>
      ),
      cell: ({ row }: { row: RowType }) => {
        const amount: number = row.original.outstandingAmount;
        return (
          <span style={{ width: "50px" }} className="text-right block">
            {formatUniversalNumber(amount)}
          </span>
        );
      },
    },
    {
      id: "amount",
      accessorKey: "",
      header: "Payment",
      cell: ({ row }: { row: RowType }) => {
        const creditMemoId = row.original.creditMemoId;
        const totalAmt = row.original.outstandingAmount;
        const paymentObj = creditMemoPayments[creditMemoId] || {
          value: "",
          totalAmt: totalAmt,
        };
        const value = paymentObj.value;

        const isChecked = row.getIsSelected?.() ?? false;

        const error = isChecked ? getPaymentError(value, totalAmt) : undefined;
        return (
          <div>
            <Input
              ref={(el) => {
                creditMemoInputRefs.current[creditMemoId] = el ?? null;
                return;
              }}
              value={value}
              onChange={(e) => {
                handlePaymentChange(
                  "memo",
                  creditMemoId,
                  e.target.value,
                  totalAmt
                );
                // Focus the input again after value change
                setTimeout(() => {
                  creditMemoInputRefs.current[creditMemoId]?.focus();
                }, 0);
              }}
              placeholder="Payment"
              className="min-w-[40px]"
              type="number"
              disabled={!isChecked}
              error={error as string | undefined}
            />
          </div>
        );
      },
    },
  ];

  const handleCloseModal = () => {
    onOpenChange(false);
    handleCloseDialog();
  };

  // Build JSON on submit
  return (
    <Dialog open={open} onOpenChange={handleCloseModal}>
      <DialogContent className="w-[1000px] max-w-[90vw] overflow-y-auto">
        <DialogHeader className="flex flex-row items-center justify-between space-y-0">
          <DialogTitle className="BookEZ_font_color_gray_2 BookEZ_font_sub_heading">
            Select Match
          </DialogTitle>
        </DialogHeader>
        <hr className="BookEZ_hr_bg_color" />
        <div className="space-y-6">
          {/* Transaction Details */}
          <Card className="">
            <CardContent className="py-0 px-2">
              <div className="grid lg:grid-cols-4! md:grid-cols-2! grid-cols-1! gap-4 text-sm">
                <div>
                  <div className="BookEZ_font_bold  BookEZ_font_header_color">
                    Date
                  </div>
                  <div className="BookEZ_font_color_gray_3 BookEZ_font_light_bold">
                    {editTransaction?.transactionDate
                      ? formatToShortDate(editTransaction?.transactionDate)
                      : "-"}
                  </div>
                </div>
                <div>
                  <div className="BookEZ_font_bold  BookEZ_font_header_color">
                    Transaction
                  </div>
                  <div
                    className="BookEZ_font_color_gray_3 BookEZ_font_light_bold truncate overflow-hidden whitespace-nowrap"
                    title={editTransaction?.personalFinanceCategoryDetailed}
                  >
                    {editTransaction?.personalFinanceCategoryDetailed}
                  </div>
                </div>
                <div>
                  <div className="BookEZ_font_bold  BookEZ_font_header_color">
                    Account
                  </div>
                  <div className="BookEZ_font_color_gray_3 BookEZ_font_light_bold">
                    {editTransaction?.merchantName}
                  </div>
                </div>
                <div>
                  <div className="BookEZ_font_bold  BookEZ_font_header_color">
                    Amount
                  </div>
                  <div className="BookEZ_font_color_gray_3 BookEZ_font_light_bold">
                    {formatUniversalNumber(editTransaction?.amount)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <hr className="BookEZ_hr_bg_color" />
          {/* invoice will come here */}
          {editTransaction && editTransaction?.amount >= 0 && (
            <Card className="">
              <CardContent className="p-0!">
                <h3 className="BookEZ_font_color_gray_2 BookEZ_font_bold  BookEZ_font_sub_heading pb-2">
                  Transactions
                </h3>
                <div className="space-y-3">
                  <div className="grid grid-cols-1! md:grid-cols-2! lg:grid-cols-4! gap-4 mb-4">
                    {/* From */}
                    <DatePicker
                      label="From"
                      showLabel={true}
                      date={transactionfilters.startDate ?? undefined}
                      maxDate={transactionfilters.endDate || undefined}
                      onDateChange={(date) =>
                        handleFilterChange("invoice", "startDate", date)
                      }
                    />

                    {/* To */}
                    <DatePicker
                      label="To"
                      showLabel={true}
                      date={transactionfilters.endDate ?? undefined}
                      minDate={transactionfilters.startDate || undefined}
                      onDateChange={(date) =>
                        handleFilterChange("invoice", "endDate", date)
                      }
                    />

                    {/* Dropdown */}
                    <CustomReactSelect
                      id="filterBy"
                      name="filterBy"
                      options={(filterByOptions.invoiceOptions as any) || []}
                      isClearable
                      label="Filter By"
                      value={
                        filterByOptions.invoiceOptions?.find(
                          (opt) => opt.value === transactionfilters.filterBy
                        ) || null
                      }
                      onChange={(
                        selected:
                          | { label: string; value: string }
                          | { label: string; value: string }[]
                          | null
                      ) => {
                        const option = Array.isArray(selected)
                          ? selected[0]
                          : selected;
                        handleFilterChange(
                          "invoice",
                          "filterBy",
                          option ? option.value : ""
                        );
                      }}
                      placeholder="Select Filter By..."
                      className="min-w-auto"
                      classNamePrefix="BookEZ_custom_select"
                    />
                    {/* Search Input */}

                    <Input
                      type="text"
                      label="Search"
                      placeholder="Search here"
                      value={transactionfilters.term || ""}
                      onChange={(e) =>
                        handleFilterChange("invoice", "term", e.target.value)
                      }
                      className="pr-10"
                      icon={<Search />}
                      // disabled={!selectedCategory || !selectedAccountId}
                    />
                  </div>
                </div>
                {isLoading?.isInvoiceLoading ? (
                  <TableSkeleton tableTheme="gray" />
                ) : (
                  <DataTable
                    rowSelection={rowSelection}
                    onRowSelectionChange={setRowSelection as any}
                    columns={InvoiceColumns}
                    data={invoices}
                    showPagination={true}
                    pageSize={pagination.pageSize}
                    currentPage={pagination.pageNumber}
                    onPageSizeChange={(size) =>
                      handlePageSizeChange("invoice", size)
                    }
                    totalPages={pagination.totalPages || 1}
                    onPageChange={(page) => handlePageChange("invoice", page)}
                    totalRecords={pagination.totalRecords || 0}
                  />
                )}
              </CardContent>
            </Card>
          )}

          {/* credit memo */}
          <Card className="">
            <CardContent className="p-0!">
              <h3 className="BookEZ_font_color_gray_2 BookEZ_font_bold  BookEZ_font_sub_heading pb-2">
                Credit Memo
              </h3>
              <div className="space-y-3">
                <div className="grid grid-cols-1! md:grid-cols-2! lg:grid-cols-4! gap-4 mb-4">
                  <DatePicker
                    label="From"
                    showLabel={true}
                    date={memofilters.startDate ?? undefined}
                    maxDate={memofilters.endDate || undefined}
                    onDateChange={(date) =>
                      handleFilterChange("credit", "startDate", date)
                    }
                  />
                  <DatePicker
                    label="To"
                    showLabel={true}
                    date={memofilters.endDate ?? undefined}
                    minDate={memofilters.startDate || undefined}
                    onDateChange={(date) =>
                      handleFilterChange("credit", "endDate", date)
                    }
                  />

                  <CustomReactSelect
                    id="filterBy"
                    name="filterBy"
                    options={filterByOptions.memoOptions || []}
                    isClearable
                    label="Filter By"
                    value={
                      filterByOptions.memoOptions?.find(
                        (opt) => opt.value === memofilters.filterBy
                      ) || null
                    }
                    onChange={(
                      selected:
                        | { label: string; value: string }
                        | { label: string; value: string }[]
                        | null
                    ) => {
                      const option = Array.isArray(selected)
                        ? selected[0]
                        : selected;
                      handleFilterChange(
                        "credit",
                        "filterBy",
                        option ? option.value : ""
                      );
                    }}
                    placeholder="Select Filter By..."
                    className="min-w-auto"
                    classNamePrefix="BookEZ_custom_select"
                  />

                  <Input
                    type="text"
                    label="Search"
                    placeholder="Search here"
                    value={memofilters.term || ""}
                    onChange={(e) =>
                      handleFilterChange("credit", "term", e.target.value)
                    }
                    className="pr-10"
                    icon={<Search />}
                  />
                </div>
                {isLoading?.isMemoLoading ? (
                  <TableSkeleton tableTheme="gray" />
                ) : (
                  <DataTable
                    rowSelection={creditMemoRowSelection}
                    onRowSelectionChange={setCreditMemoRowSelection as any}
                    columns={MemoColumns}
                    data={creditMemos}
                    showPagination={true}
                    pageSize={creditMemoPagiation.pageSize}
                    currentPage={creditMemoPagiation.pageNumber}
                    totalPages={creditMemoPagiation.totalPages || 1}
                    onPageChange={(page) => handlePageChange("memo", page)}
                    onPageSizeChange={(size) =>
                      handlePageSizeChange("memo", size)
                    }
                    totalRecords={creditMemoPagiation.totalRecords || 0}
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {error && (
            <div className="BookEZ_error_transparent border BookEZ_error_border rounded-md px-3 py-3 text-xs">
              <div className="BookEZ_error_text mb-1">{error}</div>
            </div>
          )}
          {/* Action Buttons */}
          <div className="flex justify-end items-center gap-3">
            <div className="flex gap-3 items-center">
              <Button variant="outline" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button
                variant="filled"
                type="submit"
                disabled={isLoading?.isMatchLoading}
                loading={isLoading?.isMatchLoading}
                onClick={handleMatch}
              >
                Match
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
